import { cloneElement, isValidElement, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentType, JSX } from 'react';
import {
  clefDIcone,
  cn,
  IconCalendarAdd,
  IconCog,
  IconContact,
  IconHome,
  IconModule,
  IconPlus,
  IconRabbit,
  IconSparkle,
  IconStar2,
  IconStore,
  IconTimesheet,
  IconTransferTruckTime,
  IconWarehouse1,
  TakiIcon,
  useAccesPort,
  useHoverLabel,
  usePref,
} from '@taki/ui';
import type { IconProps } from '@taki/ui';
import { raccourciUtilisable } from './raccourcis.js';
import type { AppActionDecl, ShellAppEntry } from './types.js';

/**
 * Dock d'apps vertical sur le bord gauche des surfaces de travail (croquis
 * fondateur, comportement valide en test visuel puis industrialise ici).
 *
 * Habillage PLAT : fond legerement grise (bg de la page), separe de l'app par
 * un simple filet. Grise au repos (opacity 50 %), plein au survol (300 ms),
 * regrisement 1,2 s apres la sortie de la souris, en 2 s.
 *
 * TROIS COMPORTEMENTS, au choix du poste (pref "dock.mode", reglee dans le hub
 * Parametres), voir {@link DockMode} :
 * - `legacy` : le survol n'ouvre pas tout de suite, il faut RESTER sur le rail
 *   pendant la latence d'intention ({@link DOCK_OPEN_DELAY_MS}) pour que le
 *   dock se deploie ; une souris qui ne fait que traverser ne declenche rien.
 * - `closed` (le DEFAUT) : le rail reste toujours replie, icones seules.
 * - `open` : le rail reste toujours deploye, icone + nom.
 *
 * Le regrisement, lui, est le MEME dans les trois cas : seule la largeur cesse
 * de bouger dans les modes fixes.
 *
 * Contenu, de haut en bas : logo Taki (ouvre le menu home overlay), apps
 * mises en avant (pref "dock.apps"), shortcuts d'actions (pref
 * "dock.shortcuts", VIDE tant que l'utilisateur n'en pose pas), roue Reglages
 * (navigue vers /settings).
 */

/** Nombre maximal d'apps affichees dans le dock. */
export const DOCK_APPS_MAX = 6;

/**
 * Map curatee nom -> composant des icones proposees en premier pour les
 * shortcuts du dock. Le `iconName` d'une action ou d'une pref accepte SOIT un
 * nom PascalCase de cette map, SOIT une clef kebab du registre complet de
 * @taki/ui (charge en dynamique, cf. loadIconRegistry / resolveDockIcon).
 *
 * ELLE SERT AUSSI L'ICONE D'UNE APP, derivee de sa fiche (`resolveAppEntry`) :
 * c'est ici que vivent les noms qu'aucune clef kebab ne porte, les dix alias
 * historiques (`IconContact` -> `contact-book`) et les glyphes de trait ecrits
 * a la main (`IconSparkle`). Un nom hors de cette map et hors du registre rend
 * l'icone neutre ; `pnpm apps:check` refuse d'abord une fiche qui en porte un.
 */
export const DOCK_ICONS: Record<string, ComponentType<IconProps>> = {
  IconModule,
  IconCalendarAdd,
  IconPlus,
  IconContact,
  IconRabbit,
  IconSparkle,
  IconTransferTruckTime,
  IconWarehouse1,
  IconStore,
  IconTimesheet,
  IconStar2,
  IconCog,
  IconHome,
};

/* ---- Registre complet des icones (clefs kebab), charge en dynamique ---- */

// Cache module du registre complet : une seule requete par session, partagee
// entre le dock et le hub Parametres.
let iconRegistryCache: Record<string, string> | null = null;
let iconRegistryPromise: Promise<Record<string, string>> | null = null;
let iconFondateurCache: string[] = [];

/** Les clefs des icones du fondateur (vide tant que le registre n'est pas charge). */
export function iconFondateurSnapshot(): string[] {
  return iconFondateurCache;
}

/** Registre complet deja charge, sinon null (chargement : loadIconRegistry). */
export function iconRegistrySnapshot(): Record<string, string> | null {
  return iconRegistryCache;
}

/**
 * Charge le registre complet des icones (`@taki/ui/icon-registry`) en import()
 * DYNAMIQUE : le module est lourd (~centaines de KB), il ne part jamais dans
 * le bundle du shell. Il n'arrive que quand un shortcut porte une clef kebab
 * ou quand l'utilisateur ouvre le picker complet du hub Parametres.
 */
export function loadIconRegistry(): Promise<Record<string, string>> {
  iconRegistryPromise ??= import('@taki/ui/icon-registry').then((module) => {
    iconRegistryCache = module.iconRegistry;
    iconFondateurCache = module.iconNamesFondateur;
    return module.iconRegistry;
  });
  return iconRegistryPromise;
}

/**
 * Icone du registre complet rendue depuis son inner SVG (meme convention que
 * le set @taki/ui : viewBox 24, fill currentColor, taille en rem pour suivre
 * l'echelle racine).
 */
export interface RegistryIconProps extends IconProps {
  /** Inner SVG (chaine) d'une clef kebab du registre complet. */
  inner: string;
}

export function RegistryIcon({ inner, size = 20, ...props }: RegistryIconProps): JSX.Element {
  const html = useMemo(() => ({ __html: inner }), [inner]);
  return (
    <svg
      width={`${size / 16}rem`}
      height={`${size / 16}rem`}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      {...props}
      dangerouslySetInnerHTML={html}
    />
  );
}

// Wrappers memoises des icones du registre : un composant STABLE par clef,
// sinon chaque rendu remonterait le petit svg.
const registryIconComponents = new Map<string, ComponentType<IconProps>>();

/**
 * Resout l'`iconName` d'un shortcut : nom PascalCase de la map curatee
 * DOCK_ICONS, sinon clef kebab du registre complet (quand il est charge), sinon
 * NOM DE COMPOSANT d'une fiche (`IconContactBook`, l'icone d'app qu'un
 * raccourci de porte prend d'office), sinon module neutre (qui sert aussi de
 * placeholder pendant le chargement).
 */
export function resolveDockIcon(
  name: string,
  registry?: Record<string, string> | null,
): ComponentType<IconProps> {
  const curated = DOCK_ICONS[name];
  if (curated != null) return curated;
  const inner = registry?.[name] ?? registry?.[clefDIcone(name, registry ?? null)];
  if (inner == null) return IconModule;
  let component = registryIconComponents.get(name);
  if (component == null) {
    component = function DockRegistryIcon(props: IconProps) {
      return <RegistryIcon inner={inner} {...props} />;
    };
    registryIconComponents.set(name, component);
  }
  return component;
}

/** Forme stockee dans la pref "dock.shortcuts". */
export interface DockShortcutPref {
  appId: string;
  actionId: string;
  iconName: string;
}

/**
 * LE NOM COMPLET D'UN RACCOURCI, ecrit une fois pour le dock et le panneau
 * Dock des Parametres : l'app, puis le geste (« Contact · Personnes : Ajouter »).
 * Le point median separe l'app, le deux-points reste a la porte.
 */
export function libelleDeRaccourci(appLabel: string, actionLabel: string): string {
  return `${appLabel} · ${actionLabel}`;
}

/** Shortcut resolu contre les actions declarees (libelles inclus). */
export interface ResolvedDockShortcut {
  appId: string;
  appLabel: string;
  actionId: string;
  actionLabel: string;
  iconName: string;
}

/**
 * Comportement d'ouverture du rail, pref "dock.mode" du poste :
 * - `legacy` : deploiement au survol, apres la latence d'intention.
 * - `closed` : rail toujours replie (icones seules).
 * - `open` : rail toujours deploye (icone + nom).
 */
export type DockMode = 'legacy' | 'closed' | 'open';

/** Les trois modes, dans l'ordre ou le hub Parametres les propose. */
export const DOCK_MODES: readonly DockMode[] = ['legacy', 'closed', 'open'];

/** Mode retenu quand la pref est absente ou illisible (cf. resolveDockMode). */
export const DOCK_MODE_DEFAULT: DockMode = 'closed';

/** Libelles des modes, partages par le dock et le hub Parametres : une seule
    formulation, donc jamais deux libelles divergents pour le meme mode. */
export const DOCK_MODE_LABELS: Record<DockMode, string> = {
  legacy: "Legacy : s'ouvre au survol",
  closed: 'Toujours ferme',
  open: 'Toujours ouvert',
};

/**
 * Resout la pref "dock.mode" en {@link DockMode} : la valeur telle quelle si
 * c'est une des trois chaines connues, sinon {@link DOCK_MODE_DEFAULT}.
 *
 * LE DEFAUT EST `closed`, et c'est un choix par defaut de sobriete, pas une
 * decision tranchee : le fondateur n'a pas arbitre entre les trois, et tant
 * qu'il n'a pas arbitre, un poste neuf doit montrer le rail le plus discret
 * possible (icones seules, rien qui bouge sous la souris). Le mode se change
 * ensuite dans le hub Parametres, en un geste.
 *
 * Meme raisonnement de robustesse que {@link resolveDockApps} : la pref vient
 * du localStorage, donc d'un JSON que personne ne valide, et le dock est rendu
 * par le composant de la route RACINE sans le moindre ErrorBoundary dans le
 * depot. Une exception ici ne couterait pas le rail, elle couterait
 * l'application entiere, `/settings` compris, c'est-a-dire le seul ecran par
 * lequel on aurait pu defaire la pref fautive. Cette fonction est donc PURE et
 * ne jette jamais : une pref illisible se comporte comme une pref absente.
 */
export function resolveDockMode(pref: unknown): DockMode {
  return DOCK_MODES.includes(pref as DockMode) ? (pref as DockMode) : DOCK_MODE_DEFAULT;
}

/**
 * Le rail montre-t-il le NOM d'un item au survol de son icone ?
 *
 * OUI DANS LE SEUL MODE `closed`, et la regle se lit en une phrase : une
 * etiquette apparait la ou le nom n'est JAMAIS ecrit.
 *
 * `open` : le nom est a cote de l'icone en permanence, une etiquette le
 * doublerait.
 *
 * `legacy` : le rail s'ouvre tout seul quand la souris reste, et il ecrit alors
 * les noms. Une etiquette y aurait vecu le temps de la latence d'intention
 * (100 ms) avant d'etre remplacee par le libelle du rail : un clignotement a
 * chaque survol, pour une information qui arrivait de toute facon. Ce mode
 * garde donc son `title` natif, qui ne genait personne puisqu'il ne sort
 * jamais avant l'ouverture du rail.
 *
 * `closed` : le rail ne s'ouvrira pas, le libelle y mesure zero pixel, et le
 * `title` du navigateur etait la seule facon de lire un nom. C'est le mode ou
 * l'etiquette remplace vraiment quelque chose.
 */
export function showsHoverLabel(mode: DockMode, active: boolean): boolean {
  return mode === 'closed' && active;
}

/**
 * Cle de pref de l'etiquette, convention `<domaine>.<sujet>` du repo
 * (dock.mode, dock.apps, dock.shortcuts).
 */
export const DOCK_HOVER_LABEL_PREF_KEY = 'dock.hoverLabel';

/**
 * Lecture de la pref de l'etiquette. ETAT PAR DEFAUT : ETEINTE.
 *
 * On stocke la DEVIATION, jamais l'etat normal (meme convention que `dock.mode`
 * et `apps.hidden`) : la deviation est donc `true`, qui se lit « ce poste-ci a
 * allume l'etiquette ». Eteindre efface la pref plutot que d'ecrire `false`,
 * qui serait un doublon de l'etat normal, indistinguable d'un poste neuf a la
 * relecture.
 *
 * LE DEFAUT EST ETEINT, ET C'EST LA MEME SOBRIETE QUE `DOCK_MODE_DEFAULT`.
 * Un poste neuf ne fait rien apparaitre sous la souris : le rail ferme s'en
 * tient a ses icones, et le nom d'une app reste accessible par son `title`,
 * comme avant ce chantier. Le fondateur allume l'etiquette s'il la veut, depuis
 * le panneau Dock, et ce geste est ecrit. Meme raisonnement que le mode par
 * defaut : tant qu'un comportement n'est pas arbitre, un poste neuf montre le
 * rail le plus discret.
 *
 * Meme garde de FORME que {@link resolveDockMode} : seul le booleen `true`
 * allume. Une pref absente, nulle ou corrompue (la valeur vient du
 * localStorage, donc d'un JSON qui a pu etre edite a la main) se comporte comme
 * une pref absente, donc eteinte, et rien ne jette : le dock est rendu par le
 * composant de la route RACINE, sans ErrorBoundary dans le depot.
 */
export function isHoverLabelOn(pref: unknown): boolean {
  return pref === true;
}

/**
 * Resout la pref "dock.apps" en entrees d'apps : ids valides uniquement,
 * doublons ignores, plafond {@link DOCK_APPS_MAX}. Pref absente (null) =
 * defaut : les apps installees dans l'ordre, tronquees.
 *
 * Le garde teste la FORME, pas seulement la nullite, et il descend jusqu'aux
 * ELEMENTS. La pref vient du localStorage, donc d'un JSON que personne ne
 * valide : edite a la main, ou ecrit a moitie par une version precedente. Une
 * valeur ni nulle ni tableau (`{}`, `123`) faisait jeter la boucle
 * ("not iterable"), et le dock est rendu par le composant de la route RACINE,
 * sans le moindre ErrorBoundary dans le depot : l'exception ne coutait pas le
 * dock, elle coutait l'application entiere, `/settings` compris, c'est-a-dire
 * le seul ecran par lequel on aurait pu defaire la pref fautive. Une pref
 * illisible se comporte donc comme une pref ABSENTE, et un element illisible
 * dans une pref lisible s'ignore comme un orphelin.
 */
export function resolveDockApps(
  pref: readonly string[] | null,
  entries: readonly ShellAppEntry[],
): ShellAppEntry[] {
  if (!Array.isArray(pref)) return entries.slice(0, DOCK_APPS_MAX);
  const byId = new Map(entries.map((entry) => [entry.id, entry]));
  const resolved: ShellAppEntry[] = [];
  for (const id of pref) {
    if (typeof id !== 'string') continue;
    const entry = byId.get(id);
    if (entry == null || resolved.includes(entry)) continue;
    resolved.push(entry);
    if (resolved.length >= DOCK_APPS_MAX) break;
  }
  return resolved;
}

/**
 * Resout la pref "dock.shortcuts" contre les actions declarees des apps
 * (`entry.actions`) ; les entrees orphelines (app ou action disparue) sont
 * ignorees.
 *
 * PREF ABSENTE = AUCUN RACCOURCI. C'est l'inverse de l'ancien defaut, qui
 * piochait les deux premieres actions declarees dans l'ordre du registre : le
 * dock posait donc des raccourcis que personne n'avait demandes, choisis par un
 * hasard d'ordre de fichiers, et le panneau Dock des Parametres les affichait
 * ensuite comme un choix de l'utilisateur (en deplacer un ou en retirer un
 * ecrivait la pref et figeait ce hasard pour de bon). Un raccourci se pose
 * desormais a la main, dans les Parametres, et le bloc entier n'existe pas tant
 * qu'il n'y en a aucun.
 *
 * Le garde teste la FORME, pas seulement la nullite, et il descend jusqu'aux
 * ELEMENTS : la pref vient du localStorage, donc d'un JSON qui a pu etre edite
 * a la main. Sur une valeur ni nulle ni tableau (`{}`, `123`), la boucle
 * `for...of` jetterait "not iterable" ; sur un tableau qui contient autre chose
 * que des entrees (`[null]`), c'est la lecture de `item.appId` qui jetterait.
 * Les deux tombent depuis le dock, rendu par le composant de la route RACINE et
 * sans aucun ErrorBoundary dans le depot : ce n'est pas le rail qui disparait,
 * c'est l'application entiere, `/settings` compris, donc l'unique ecran ou l'on
 * pourrait defaire la pref fautive. Une pref illisible se comporte donc comme
 * une pref absente (aucun raccourci), et une ENTREE illisible s'ignore comme un
 * orphelin. Jamais une exception.
 */
export function resolveDockShortcuts(
  pref: readonly DockShortcutPref[] | null,
  entries: readonly ShellAppEntry[],
  /** Ne garder que les raccourcis dont ce membre peut se servir (le dock). */
  utilisable: (action: AppActionDecl) => boolean = () => true,
): ResolvedDockShortcut[] {
  if (!Array.isArray(pref)) return [];
  const byId = new Map(entries.map((entry) => [entry.id, entry]));
  const resolved: ResolvedDockShortcut[] = [];
  for (const item of pref) {
    if (item == null || typeof item !== 'object') continue;
    const entry = byId.get(item.appId);
    const action = entry?.actions?.find((declared) => declared.id === item.actionId);
    if (entry == null || action == null || !utilisable(action)) continue;
    resolved.push({
      appId: entry.id,
      appLabel: entry.label,
      actionId: action.id,
      actionLabel: action.label,
      iconName: item.iconName,
    });
  }
  return resolved;
}

/** Pastille du dock : Carres doux, gris neutres et contour transparent.
    La pastille est TOUJOURS en pleine
    largeur du rail (replie, ca fait 36 px = la colonne d'icone seule) : comme
    seule la largeur du rail s'anime, la pastille la suit image par image, sans
    jamais re-passer par un etat « centre » qui ferait sauter l'icone au repli.
    L'icone garde sa colonne de 36 px a gauche, deploye ou non. */
function cnDock(active: boolean, awake = false): string {
  return cn(
    'flex h-9 w-full items-center gap-2.5 rounded-nav-app pr-2.5 transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-focus',
    'active:bg-nav-press active:text-nav-ink',
    // La chip active suit le MEME tempo que le fondu du dock (300 ms a
    // l'eveil, 2 s au regrissement), sinon elle semble regriser avant le reste.
    active
      ? awake
        ? 'bg-nav-dock-active text-nav-dock-ink duration-300'
        : 'bg-nav-dock-idle text-nav-dock-ink duration-[2000ms]'
      : 'text-nav-muted duration-150 hover:bg-nav-hover hover:text-nav-ink',
  );
}

/**
 * Contenu d'une ligne du dock : la colonne d'icone (36 px, fixe) puis le
 * libelle, TOUJOURS monte (jamais demonte/remonte) : c'est le bord anime du
 * rail qui le revele ou l'avale (troncature), double d'un fondu d'opacite au
 * meme tempo (300 ms). Replie, il mesure 0 px et ne peint rien. L'icone reste
 * a la meme abscisse, deploye ou non.
 */
function DockRowInner({
  icon,
  label,
  expanded,
  badge = false,
}: {
  icon: JSX.Element;
  label: string;
  expanded: boolean;
  badge?: boolean;
}): JSX.Element {
  return (
    <>
      <span className="relative flex h-9 w-9 shrink-0 items-center justify-center">
        {icon}
        {badge && <DockPlusBadge />}
      </span>
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-left text-[0.8125rem] font-medium',
          'transition-opacity duration-300 ease-in-out',
          expanded ? 'opacity-100' : 'opacity-0',
        )}
      >
        {label}
      </span>
    </>
  );
}

/** Petit filet horizontal separant les groupes du dock. */
function DockRule(): JSX.Element {
  return <span aria-hidden className="my-1 h-px w-6 shrink-0 self-center bg-nav-separator" />;
}

/** Badge lime « + » des shortcuts de creation du dock. */
function DockPlusBadge(): JSX.Element {
  return (
    <span
      className={cn(
        'absolute -right-[0.125rem] -top-[0.125rem] flex h-[0.875rem] w-[0.875rem] items-center justify-center',
        'rounded-full border-[1.5px] border-surface bg-accent text-accent-label',
      )}
    >
      <IconPlus size={7} />
    </span>
  );
}

export interface DockProps {
  /** Apps installees, dans l'ordre de la liste de l'org (`settings/apps`). */
  appEntries: readonly ShellAppEntry[];
  /** Id de l'app couramment montee (chip active), absent sur /settings. */
  activeAppId?: string;
  /** Selection d'une app : le shell navigue vers /app/<id>. */
  onPickApp: (appId: string) => void;
  /** Clic d'un shortcut : le shell pose la pending action puis navigue. */
  onPickShortcut: (appId: string, actionId: string) => void;
  /** Clic sur la marque, en haut du rail : ouvre le MENU HOME overlay. */
  onOpenHomeMenu: () => void;
  /**
   * L'ICONE DU CLIENT, en haut du rail, a la place du chevron Taki. `null`
   * quand l'environnement n'en a pas choisi : le chevron Taki tient alors la
   * place, comme avant ce reglage. C'est une data URL (ou une URL de la config
   * de deploiement), calculee par `TakiShell` depuis le branding.
   */
  iconeClient?: string | null;
  /** Clic sur la roue Reglages : le shell navigue vers /settings. */
  onOpenSettings: () => void;
  /**
   * La cloche des notifications, rendue au-dessus de Reglages avec la classe
   * d'une ligne du dock (`ClocheNotifications`, `src/notifications/`). Absente :
   * pas de cloche (un hote sans port de notifications).
   */
  renderCloche?: (dock: { expanded: boolean; className: string }) => JSX.Element | null;
}

/** Delai avant regrisement une fois la souris sortie (ms). */
const DOCK_SLEEP_DELAY_MS = 1200;

/**
 * Latence d'INTENTION avant ouverture au survol (ms) : le dock ne s'ouvre que
 * si la souris RESTE sur le rail, pas quand elle le traverse pour aller
 * ailleurs. Le compte a rebours est annule des que le pointeur ressort.
 *
 * 100 ms, cale au plus court apres test a l'oeil : l'ouverture reste percue
 * comme immediate quand on vise le dock, tout en coupant les traversees les
 * plus vives. C'est le plancher utile : une souris lente qui traverse le rail
 * replie met a peu pres ce temps a franchir ses 56 px, donc en dessous le
 * filtre ne filtre plus rien. Monter vers 150 ou 250 ms si un simple passage
 * ouvre encore par accident.
 */
const DOCK_OPEN_DELAY_MS = 100;

/** Vrai si le pointeur est encore dans le rectangle du rail. Sert a demasquer
    les sorties FANTOMES : quand la page bouge sous un curseur immobile (clic
    qui navigue, re-rendu), le navigateur peut emettre un mouseleave alors que
    la souris n'a pas bouge. La position reelle du pointeur, elle, ne ment
    pas : c'est LA source de verite du repli. */
function pointerInside(rect: DOMRect, x: number, y: number): boolean {
  return x >= rect.left && x < rect.right && y >= rect.top && y < rect.bottom;
}

export function Dock({
  appEntries,
  activeAppId,
  onPickApp,
  onPickShortcut,
  onOpenHomeMenu,
  iconeClient = null,
  onOpenSettings,
  renderCloche,
}: DockProps): JSX.Element {
  // Comportement d'ouverture choisi sur ce poste. Lu tot : c'est lui qui dit
  // si la largeur bouge (legacy) ou reste figee (closed / open), donc tous les
  // handlers ci-dessous en dependent. Pref illisible = le defaut sobre.
  const [modePref] = usePref<unknown>('dock.mode', null);
  const mode = resolveDockMode(modePref);

  // Le dock est grise au repos, plein au survol, et reste plein 1,2 s apres
  // la sortie de la souris avant de regriser en 2 s. Ce regrisement vaut dans
  // les TROIS modes : c'est la largeur, pas la lumiere, qui se fige.
  const [dockAwake, setDockAwake] = useState(false);
  // Deploiement du mode `legacy` uniquement : au survol (ou focus clavier), le
  // rail s'elargit vers la droite pour afficher le nom de chaque icone.
  // Contrairement au regrisement, il se replie DES la sortie de la souris (la
  // surface de travail voisine reprend aussitot la largeur liberee), tant que
  // dockAwake est encore vrai : la meme transition (300 ms) porte donc le
  // repli, avant que le regrisement 2 s ne prenne le relais une fois la
  // largeur deja revenue. Dans `closed` et `open`, cet etat n'est pas lu.
  const [expandedState, setExpandedState] = useState(false);
  // Vrai entre l'entree de la souris et l'ouverture reelle, pendant que court
  // la latence d'intention. C'est un ETAT et pas une ref : le filet de
  // securite ci-dessous doit se rebrancher pendant cette fenetre.
  const [openPending, setOpenPending] = useState(false);
  const asideRef = useRef<HTMLElement | null>(null);
  const dockTimer = useRef<number | undefined>(undefined);
  const openTimer = useRef<number | undefined>(undefined);
  const dockHovered = useRef(false);
  const cancelDockSleep = (): void => {
    if (dockTimer.current != null) window.clearTimeout(dockTimer.current);
    dockTimer.current = undefined;
  };
  const wakeDock = (): void => {
    cancelDockSleep();
    setDockAwake(true);
  };
  const sleepDock = (): void => {
    cancelDockSleep();
    dockTimer.current = window.setTimeout(() => setDockAwake(false), DOCK_SLEEP_DELAY_MS);
  };
  /** Desarme une ouverture programmee mais pas encore survenue. */
  const cancelDockOpen = (): void => {
    if (openTimer.current != null) window.clearTimeout(openTimer.current);
    openTimer.current = undefined;
    setOpenPending(false);
  };
  /** Ouverture IMMEDIATE (eveil + deploiement), sans latence : reservee au
      focus clavier, qui ne "passe" jamais sur le dock par accident. */
  const openDockNow = (): void => {
    cancelDockOpen();
    wakeDock();
    setExpandedState(true);
  };
  /** Ouverture au SURVOL : programmee, puis honoree seulement si la souris est
      restee sur le rail pendant toute la latence d'intention. */
  const openDockAfterDelay = (): void => {
    if (openTimer.current != null) window.clearTimeout(openTimer.current);
    setOpenPending(true);
    openTimer.current = window.setTimeout(openDockNow, DOCK_OPEN_DELAY_MS);
  };
  useEffect(
    () => () => {
      if (dockTimer.current != null) window.clearTimeout(dockTimer.current);
      if (openTimer.current != null) window.clearTimeout(openTimer.current);
    },
    [],
  );

  // Largeur REELLE du rail. En `legacy` elle suit l'etat de survol ; dans les
  // deux modes fixes, elle ne depend que du mode : l'etat existe toujours mais
  // n'est plus lu, donc aucun timer en vol ne peut elargir le rail.
  const expanded = mode === 'legacy' ? expandedState : mode === 'open';

  /* L'ETIQUETTE AU SURVOL, dans le seul mode ou le nom n'est ecrit nulle part
     (voir showsHoverLabel). Un seul etat pour tout le rail : glisser d'une
     icone a l'autre DEPLACE l'etiquette au lieu d'en fermer une et d'en ouvrir
     une autre. Elle vit dans un portal, donc elle echappe a l'opacite du rail
     et a sa largeur de 56 px : le detail est explique dans HoverLabel.tsx. */
  const [hoverLabelPref] = usePref<unknown>(DOCK_HOVER_LABEL_PREF_KEY, null);
  const etiquetteActive = showsHoverLabel(mode, isHoverLabelOn(hoverLabelPref));
  const etiquette = useHoverLabel(etiquetteActive);

  /* LE `title` NATIF S'EFFACE DEVANT L'ETIQUETTE, jamais les deux ensemble :
     le navigateur sortirait sa propre bulle une seconde apres la notre, a deux
     centimetres de la, dans la police du systeme. Dans les deux autres modes il
     reste : en `open` il ne sort jamais (le nom est deja lisible, personne ne
     s'arrete sur l'icone), en `legacy` il est double par l'ouverture du rail.
     Et il est la en `closed` tant que l'etiquette n'a pas ete allumee, ce qui
     est l'etat d'un poste neuf : un rail sans etiquette ne doit pas etre muet,
     il reste ce qu'il etait avant ce chantier.
     L'`aria-label` du bouton, lui, ne bouge dans aucun mode : c'est lui le nom
     accessible, et l'etiquette est `aria-hidden`. */
  const titreNatif = (label: string): { title?: string } =>
    etiquetteActive ? {} : { title: label };

  // Bascule de mode EN DIRECT (le fondateur change le reglage dans /settings,
  // ou le dock est monte : usePref se resynchronise par l'event). Un timer
  // d'ouverture arme sous l'ancien mode laisserait sinon le rail dans un etat
  // incoherent des le retour en `legacy` : on desarme et on repart replie.
  useEffect(() => {
    cancelDockOpen();
    setExpandedState(false);
    // cancelDockOpen ne lit que des refs : pas une vraie dependance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Filet de securite du repli, utile au SEUL mode `legacy` (ailleurs la
  // largeur ne bouge pas, il n'y a rien a replier) : tant que le dock est
  // deploye OU qu'une ouverture est armee, le premier mouvement de souris HORS
  // du rail le replie et desarme l'ouverture, meme si le navigateur a avale la
  // vraie sortie pendant un re-rendu. Un mousemove porte la position REELLE du
  // pointeur : contrairement aux enter/leave, il ne ment jamais.
  useEffect(() => {
    if (mode !== 'legacy') return;
    if (!expanded && !openPending) return;
    const onMove = (event: MouseEvent): void => {
      const rect = asideRef.current?.getBoundingClientRect();
      if (rect == null || pointerInside(rect, event.clientX, event.clientY)) return;
      dockHovered.current = false;
      cancelDockOpen();
      sleepDock();
      setExpandedState(false);
    };
    document.addEventListener('mousemove', onMove);
    return () => document.removeEventListener('mousemove', onMove);
    // sleepDock / cancelDockOpen ne lisent que des refs : pas de vraies
    // dependances.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, expanded, openPending]);

  // Prefs locales du poste : les usePref se resynchronisent en direct quand
  // le hub Parametres ecrit (event "taki:pref-change").
  const [appsPref] = usePref<string[] | null>('dock.apps', null);
  const [shortcutsPref] = usePref<DockShortcutPref[] | null>('dock.shortcuts', null);

  const dockApps = useMemo(() => resolveDockApps(appsPref, appEntries), [appsPref, appEntries]);
  /* UN RACCOURCI VERS UNE PORTE OU CE MEMBRE N'ECRIT PAS N'EST PAS MONTRE : il
     ouvrirait la porte sans rien y creer (`raccourciUtilisable`). La pref, elle,
     le garde : le jour ou le droit s'ouvre, il revient a sa place. */
  const acces = useAccesPort();
  const shortcuts = useMemo(
    () =>
      resolveDockShortcuts(shortcutsPref, appEntries, (action) => raccourciUtilisable(action, acces)),
    [shortcutsPref, appEntries, acces],
  );

  // Registre complet charge en dynamique au PREMIER shortcut kebab rencontre
  // (clef hors DOCK_ICONS) ; IconModule sert de placeholder le temps du
  // chargement, resolveDockIcon bascule des que le registre arrive.
  const [iconRegistry, setIconRegistry] = useState(iconRegistrySnapshot);
  const needsIconRegistry = shortcuts.some((shortcut) => DOCK_ICONS[shortcut.iconName] == null);
  useEffect(() => {
    if (!needsIconRegistry || iconRegistry != null) return;
    let cancelled = false;
    void loadIconRegistry().then((registry) => {
      if (!cancelled) setIconRegistry(registry);
    });
    return () => {
      cancelled = true;
    };
  }, [needsIconRegistry, iconRegistry]);

  // Le bloc des raccourcis n'a plus de repli : les 2 chips decoratives du test
  // fondateur (New item / New reservation) s'affichaient tant qu'aucune pref
  // n'etait posee, c'est-a-dire dans le cas NORMAL depuis que le defaut est
  // tombe a zero. Elles auraient donc menti en permanence : deux boutons morts,
  // sans clic, que rien dans les Parametres ne permettait de retirer. Le groupe
  // (filet compris) existe si et seulement si un raccourci est configure.
  const hasShortcutsBlock = shortcuts.length > 0;

  return (
    <aside
      ref={asideRef}
      aria-label="Apps installees"
      onMouseEnter={() => {
        dockHovered.current = true;
        // En `legacy`, rien ne s'ouvre encore : on ARME l'ouverture, elle
        // n'aura lieu que si la souris est toujours la au bout de la latence
        // d'intention. Dans les modes fixes, cette latence n'a plus d'objet
        // (elle ne sert qu'a eviter d'OUVRIR par accident) : le rail s'eveille
        // tout de suite, sa largeur ne bougeant de toute facon pas.
        if (mode === 'legacy') openDockAfterDelay();
        else wakeDock();
      }}
      onMouseLeave={(event) => {
        // Sortie FANTOME : coordonnees encore dans le rail = la souris n'a
        // pas bouge, c'est la page qui a change dessous (clic sur une app,
        // navigation). On ignore : un clic ne referme JAMAIS le dock, seule
        // une vraie sortie de la souris le fait. Une ouverture armee survit
        // donc au clic, mais pas a une vraie sortie.
        const rect = event.currentTarget.getBoundingClientRect();
        if (pointerInside(rect, event.clientX, event.clientY)) return;
        dockHovered.current = false;
        cancelDockOpen();
        sleepDock();
        setExpandedState(false);
        /* Filet : si le pointeur quitte le rail entre deux items (par le bord),
           aucun bouton ne recoit de mouseleave et l'etiquette resterait. */
        etiquette.clear();
      }}
      onFocusCapture={() => {
        // Le focus clavier eveille dans les trois modes, mais il ne DEPLOIE
        // qu'en `legacy` : ailleurs la largeur est un choix du poste, pas
        // quelque chose qu'une tabulation renegocie.
        if (mode === 'legacy') openDockNow();
        else wakeDock();
      }}
      onBlurCapture={(event) => {
        // Ne regrise / ne replie pas tant que la souris est sur le dock, ni
        // quand le focus reste a l'interieur (clic dans le vide).
        if (dockHovered.current) return;
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        cancelDockOpen();
        sleepDock();
        setExpandedState(false);
      }}
      className={cn(
        // Le fond reste opaque ; seul le contenu s'attenue au repos.
        // La largeur garde son tempo de 300 ms dans tous les modes.
        'relative flex shrink-0 flex-col items-stretch bg-nav-dock transition-[width] duration-300 ease-in-out',
        expanded ? 'w-52' : 'w-[3.5rem]',
      )}
    >
      {/* Filet de separation, dessine par-dessus le bord droit (cf. commentaire
          de la classe du rail : une bordure decalerait les icones d'1 px). */}
      <span aria-hidden className="absolute inset-y-0 right-0 w-px bg-nav-border" />

      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col items-stretch gap-1.5 px-2.5 pb-3 pt-4 transition-opacity ease-in-out',
          dockAwake ? 'opacity-100 duration-300' : 'opacity-50 duration-[2000ms]',
        )}
      >
      {/* LA MARQUE, en haut du rail : ouvre le menu home (overlay du panneau
          Home). C'est l'icone du CLIENT quand il en a choisi une (Parametres >
          Environnement), le chevron Taki sinon. Les deux tiennent le meme
          carre de 20 px : une icone plus large est contenue (`object-contain`),
          jamais deformee ni rognee. */}
      <button
        type="button"
        aria-label="Menu home"
        {...titreNatif('Home')}
        {...etiquette.trigger('Home')}
        onClick={onOpenHomeMenu}
        className={cn(
          'flex h-9 w-full items-center gap-2.5 rounded-nav-app pr-2.5 text-nav-text',
          'transition-colors duration-300 ease-in-out hover:bg-nav-hover hover:text-nav-ink active:bg-nav-press',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-focus',
        )}
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center">
          {iconeClient === null ? (
            <TakiIcon width="1.25rem" height="1.25rem" />
          ) : (
            <img
              src={iconeClient}
              alt=""
              aria-hidden
              className="h-5 w-5 object-contain"
              /* Une icone est un petit carre : le navigateur ne doit pas la
                 lisser au point de la rendre floue quand elle est nette. */
              draggable={false}
            />
          )}
        </span>
        <span
          className={cn(
            'min-w-0 flex-1 truncate text-left text-[0.8125rem] font-semibold',
            'transition-opacity duration-300 ease-in-out',
            expanded ? 'opacity-100' : 'opacity-0',
          )}
        >
          Home
        </span>
      </button>

      <DockRule />

      {/* Apps mises en avant (pref "dock.apps", defaut = installees, max 6). */}
      {dockApps.map((entry) => {
        const active = entry.id === activeAppId;
        return (
          <button
            key={entry.id}
            type="button"
            aria-label={entry.label}
            {...titreNatif(entry.label)}
            {...etiquette.trigger(entry.label)}
            aria-current={active ? 'true' : undefined}
            onClick={() => onPickApp(entry.id)}
            className={cnDock(active, dockAwake)}
          >
            {/* Icone du registre reduite pour la pastille du dock. */}
            <DockRowInner
              icon={
                isValidElement(entry.icon) ? (
                  cloneElement(entry.icon as JSX.Element, { size: 17 })
                ) : (
                  <>{entry.icon}</>
                )
              }
              label={entry.label}
              expanded={expanded}
            />
          </button>
        );
      })}

      {hasShortcutsBlock && <DockRule />}

      {/* Shortcuts d'actions (pref "dock.shortcuts") : le clic pose la
          pending action ({ appId, actionId }) puis le shell navigue ; l'app
          consomme l'action au montage et ouvre son Modal. */}
      {shortcuts.map((shortcut) => {
        const Icon = resolveDockIcon(shortcut.iconName, iconRegistry);
        const label = libelleDeRaccourci(shortcut.appLabel, shortcut.actionLabel);
        return (
          <button
            key={`${shortcut.appId}:${shortcut.actionId}`}
            type="button"
            aria-label={label}
            {...titreNatif(label)}
            {...etiquette.trigger(label)}
            onClick={() => onPickShortcut(shortcut.appId, shortcut.actionId)}
            className={cnDock(false, dockAwake)}
          >
            <DockRowInner icon={<Icon size={17} />} label={label} expanded={expanded} badge />
          </button>
        );
      })}

      <div className="flex-1" />

      {renderCloche?.({ expanded, className: cnDock(false, dockAwake) })}

      {/* Reglages : le hub Parametres du shell (/settings). */}
      <button
        type="button"
        aria-label="Reglages"
        {...titreNatif('Reglages')}
        {...etiquette.trigger('Reglages')}
        onClick={onOpenSettings}
        className={cnDock(false, dockAwake)}
      >
        <DockRowInner icon={<IconCog size={17} />} label="Reglages" expanded={expanded} />
      </button>

      </div>

      {/* L'etiquette, rendue UNE fois pour tout le rail. Elle sort d'ici par un
          portal : sa place dans ce JSX ne decide ni de sa position, ni de son
          empilement. */}
      {etiquette.overlay}
    </aside>
  );
}
